<template>
    <div :id="parentId" class="listing-collections-carousel-insider">
        <Swiper
            ref="swiperRef"
            :modules="modules"
            :slides-per-view="slidesPerView"
            :slides-per-group="slidesPerGroup"
            :breakpoints="breakpoints"
            :loop="loop"
            class="insider-swiper"
            @swiper="onSwiperReady"
        >
            <SwiperSlide v-for="item in dataListing" :key="item.item_id">
                <div class="custom_item_is" :class="isMobile ? 'ins-mobile-web-smart-recommender-box-item' : 'ins-web-smart-recommender-box-item'">
                    <ProductItemSearch :item="item" />
                </div>
            </SwiperSlide>
        </Swiper>
        <div class="box-controls" v-if="shouldShowNavigation">
            <div class="box-btn">
                <div class="swiper-button swiper-button-prev-list" @click="handlePrevClick">
                    <svg
                    fill="#000000"
                    viewBox="0 0 24.00 24.00"
                    id="right-2"
                    data-name="Flat Line"
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon flat-line"
                    transform="rotate(180)"
                    >
                    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                    <g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke="#CCCCCC"
                        stroke-width="0.048"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                        <polyline
                        id="primary"
                        points="7.5 3 16.5 12 7.5 21"
                        style="
                            fill: none;
                            stroke: rgb(0, 0, 0);
                            stroke-linecap: round;
                            stroke-linejoin: round;
                            stroke-width: 0.816;
                        "
                        ></polyline>
                    </g>
                    </svg>
                </div>
            </div>
            <div class="box-btn">
            <div class="swiper-button swiper-button-next-list" @click="handleNextClick">
                <svg
                fill="#000000"
                viewBox="0 0 24.00 24.00"
                id="right-2"
                data-name="Flat Line"
                xmlns="http://www.w3.org/2000/svg"
                class="icon flat-line"
                >
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g
                    id="SVGRepo_tracerCarrier"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke="#CCCCCC"
                    stroke-width="0.048"
                ></g>
                <g id="SVGRepo_iconCarrier">
                    <polyline
                    id="primary"
                    points="7.5 3 16.5 12 7.5 21"
                    style="
                        fill: none;
                        stroke: rgb(0, 0, 0);
                        stroke-linecap: round;
                        stroke-linejoin: round;
                        stroke-width: 0.816;
                    "
                    ></polyline>
                </g>
                </svg>
            </div>
            </div>
        </div>
    </div>
</template>
<script setup>
    import { ref, onMounted, watch, computed, onUnmounted, nextTick } from 'vue';
    import { Swiper, SwiperSlide } from "swiper/vue";
    import { FreeMode, Navigation } from "swiper/modules";
    import ProductItemSearch from "~/components/Product/ProductItemSearch.vue";

    import 'swiper/css/free-mode';
    import "swiper/css";
    import "swiper/css/navigation";
   
    const isMobile = ref(false);

    const props = defineProps({
        dataListing: {
            type: Array,
            default: () => []
        },
        modules: {
            type: Array,
            default: () => [Navigation]
        },
        slidesPerView: {
            type: Number,
            default: 6
        },
        slidesPerGroup: {
            type: Number,
            default: 1
        },
        breakpoints: {
            type: Object,
            default: () => ({
                320: { slidesPerView: 2, spaceBetween: 20, slidesPerGroup: 1 },
                767: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 20 },
                1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 15 },
                1280: { slidesPerView: 6, spaceBetween: 12 },
            })
        },
        parentId: {
            type: String,
            default: 'insider_search_crousel'
        },
        loop: {
            type: Boolean,
            default: false
        },
        buttonTopOffset: {
            type: Number,
            default: 0,
            description: 'Additional offset to adjust button top position in px'
        }

    });

    const isDataReady = ref(false);
    const swiperInstance = ref(null);

    const checkIsMobile = () => {
        isMobile.value = window.innerWidth < 769;
    };

    const shouldShowNavigation = computed(() => {
        return props.dataListing.length > props.slidesPerView;
    });

    const onSwiperReady = (swiper) => {
        swiperInstance.value = swiper;
    };

    const handlePrevClick = () => {
        if (swiperInstance.value) {
            swiperInstance.value.slidePrev();
        }
    };

    const handleNextClick = () => {
        if (swiperInstance.value) {
            swiperInstance.value.slideNext();
        }
    };

    const handleStyleTopButton = () => {
      const product = document.querySelector(`#${props.parentId} .swiper-slide .product-image-wrapper`);
      const buttons = document.querySelectorAll(`#${props.parentId} .box-controls .swiper-button`);
      
      // Only update if product is visible (has height)
      if (!product || product.clientHeight === 0) {
        return;
      }

      const productHeight = product.clientHeight || 0;
      const topPosition = (productHeight / 2) + props.buttonTopOffset;

      buttons.forEach(btn => {
        btn.style.top = topPosition + "px";
      });
    };

    watch(() => props.dataListing, async () => {
        await nextTick();
        setTimeout(() => {
            handleStyleTopButton();
        }, 200);
    }, { deep: true });

    watch(() => props.buttonTopOffset, () => {
        handleStyleTopButton();
    });

    onMounted(() => {
        isDataReady.value = true;
        checkIsMobile();
        // Trigger style calculation on mount
        setTimeout(() => {
            handleStyleTopButton();
        }, 300);
        
        // Listen for tab switch event to recalculate button position
        window.addEventListener('recalculateButtonPosition', () => {
            setTimeout(() => {
                handleStyleTopButton();
            }, 50);
        });
        
        window.addEventListener('resize', checkIsMobile);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', checkIsMobile);
        window.removeEventListener('recalculateButtonPosition', handleStyleTopButton);
    });
</script>
<style lang="scss" scoped>
    @import "@/assets/scss/components/listing/listingItem.scss";

    .insider-swiper {
        width: 100%;
        padding: 20px 0;

        :deep(.swiper-slide) {
            height: auto;
            display: flex;
            align-items: flex-start !important;
        }

        :deep(.swiper-wrapper) {
            align-items: flex-start !important;
        }
    }
    .listing-collections-carousel-insider {
        position: relative;
        .box-controls {
            .box-btn {
                @media screen and (max-width: 991px) {
                    display: none;
                }
                .swiper-button {
                    position: absolute;
                    top: 28%;
                    border: 1px solid #ddd;
                    background: hsla(0, 0%, 100%, 0.5);
                    height: 50px;
                    padding: 14px 4px;
                    width: 28px;
                    border-radius: 5px;
                    z-index: 9;
                    cursor: pointer;

                    &.swiper-button-prev-list {
                        left: 0;
                        svg {
                            transform: rotate(180deg);
                        }
                    }
                     &.swiper-button-next-list {
                        right: 0;
                    }
                }
            }
        }
    }

    .custom_item_is{
        width: 100%;
        height: 100%;
        display: flex;
    }

    .ins-web-smart-recommender-box-item {
        width: 100%;
        height: 100%;
        display: flex;
    }

    .ins-mobile-web-smart-recommender-box-item {
        width: 100%;
        height: 100%;
        display: flex;
    }

    .ins-product-box {
        width: 100%;
        display: flex;
        flex-direction: column;
    }

</style>